import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { Room, RoomEvent, Track, ConnectionState } from 'livekit-client';
import { fetchLiveKitToken } from '../utils/livekitService';
import '../styles/reading/ReadingSession.css';
import '../styles/quiz/Quiz.css';
import Header from '../components/common/Header';
import LeftSection from '../components/reading/LeftSection';
import RightSection from '../components/reading/RightSection';
import Clock from '../components/reading/Clock';
import ModalPortal from '../components/quiz/ModalPortal';
import QuizInterface from '../components/quiz/QuizInterface';
/*import QuizDashboard from '../components/quiz/QuizDashboard';*/
import { initSocket, sendControl, sendScreenshot, closeSocket } from '../utils/socket';
import { refreshCache } from '../utils/cacheService';
import { getApiUrl } from '../utils/apiConfig';
import { FINAL_QUIZ_INDEX, EXTRA_QUIZ_COUNT_KEY } from '../config/quizConfig';

function ReadingSession() {
  const navigate = useNavigate();
  
  // Toggle for CV status indicator (set to false to hide debugging info)
  const SHOW_CV_STATUS_INDICATOR = false;
  const QUIZ_SOURCE = process.env.REACT_APP_QUIZ_SOURCE || 'ocr';
  const useOCRPipeline = QUIZ_SOURCE === 'ocr';
  const QUIZ_MODE = process.env.REACT_APP_QUIZ_MODE || 'timed';
  
  // NO automatic cache clearing - user must manually refresh browser (CMD+R / CTRL+F5)
  // The browser's native refresh will restart the React app with clean state
  const [content, setContent] = useState('');
  const [file, setFile] = useState(null);
  const [showLeft, setShowLeft] = useState(true);
  const [topicUrl, setTopicUrl] = useState(null);
  
  // Callback to refresh subject scores when quiz results are saved
  const handleQuizResultSaved = () => {
    console.log('🔄 Quiz result saved - subject scores will be refreshed automatically');
    // The refresh will happen automatically through the useSubjectScores hook
    // This is just a placeholder for future enhancements
  };
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [startTimer, setStartTimer] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [currentQuizIndex, setCurrentQuizIndex] = useState(0);
  const [showDashboard, setShowDashboard] = useState(false);
  const [textHash, setTextHash] = useState(null);         // For Quiz 1–5 (hash per quiz_request)
  const [sessionHashes, setSessionHashes] = useState([]);   // For Final Quiz
  // Content hashes and questions already quizzed this session, so each quiz
  // picks the newest unseen content and avoids repeating a question.
  const quizHistoryRef = useRef({ hashes: new Set(), questions: new Set() });
  // On-demand extra quizzes (timed mode): which one is open (null = a scheduled quiz),
  // how many were taken, and a scheduled quiz that came due while an extra was open.
  const [extraQuizNumber, setExtraQuizNumber] = useState(null);
  const extraQuizCountRef = useRef(0);
  const extraQuizOpenRef = useRef(false);
  const pendingScheduledQuizRef = useRef(null);

  // CV Webcam state
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const wsRef = useRef(null);
  const frameTimerRef = useRef(null);
  const cvStreamRef = useRef(null);
  const [cvStatus, setCvStatus] = useState('idle');
  const [accessToken, setAccessToken] = useState(null);
  const cvIntentionallyStoppedRef = useRef(false); // Track if CV was intentionally stopped
  const reconnectTimeoutRef = useRef(null); // Track reconnection timeout
  const reconnectAttemptsRef = useRef(0); // Track reconnection attempts

  // LiveKit voice assistant state
  const liveKitRoomRef = useRef(null);
  const [liveKitConnectionState, setLiveKitConnectionState] = useState(ConnectionState.Disconnected);
  const [liveKitTranscripts, setLiveKitTranscripts] = useState([]);
  const [liveKitStatus, setLiveKitStatus] = useState('idle');
  const [isMicrophoneMuted, setIsMicrophoneMuted] = useState(true);
  
  // Audio analysis refs for LiveKit
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const visualizerCanvasRef = useRef(null);
  const animationFrameRef = useRef(null);

  // Agent service WebSocket (for inactivity-based auto mute)
  const agentSocketRef = useRef(null);

  // Calibration state
  const [calibrationStatus, setCalibrationStatus] = useState('idle'); // 'idle', 'starting', 'running', 'complete'
  const [spritePosition, setSpritePosition] = useState('center');
  const [calibrationCountdown, setCalibrationCountdown] = useState(5);
  const [showCalibrationAnimation, setShowCalibrationAnimation] = useState(false);
  const calibrationTimerRef = useRef(null);
  // Tracks whether calibration has already completed once for this mounted
  // reading session, so a CV reconnect that reuses the existing backend
  // session (status "already_running") doesn't replay the animation.
  const calibrationDoneRef = useRef(false);
  // Tracks whether a calibration animation is currently mid-sequence, so a
  // WS reconnect that lands *during* calibration (brief disconnects can
  // happen repeatedly in that ~15s window, e.g. on Windows) doesn't start a
  // second overlapping animation stacking its own timers on top of the
  // first - that's what made the sprite jump around erratically.
  const calibrationInProgressRef = useRef(false);

  // Avatar overlay animation state - default to small
  const [avatarOverlayLarge, setAvatarOverlayLarge] = useState(false);

  // Screen capture refs
  const captureIntervalRef = useRef(null);
  const streamRef = useRef(null);
  const isCapturePausedRef = useRef(false);
  // True when screen sharing ended mid-session; shows the "Resume screen sharing" banner.
  const [screenShareLost, setScreenShareLost] = useState(false);

  // Screen capture functions. Returns true if sharing started.
  // `resuming` re-shares mid-session without touching the session clock or quiz schedule.
  const startContinuousCapture = async ({ resuming = false } = {}) => {
    try {
      const mediaStream = await navigator.mediaDevices.getDisplayMedia({
        video: { 
          mediaSource: "screen",
          frameRate: { ideal: 1 }
        }
      });

      streamRef.current = mediaStream;
      const track = mediaStream.getVideoTracks()[0];
      const imageCapture = new ImageCapture(track);
      
      // Detect when sharing ends outside the app (browser "Stop sharing", shared
      // window closed, OS permission revoked). Offer a one-click resume instead of
      // making the student restart the session.
      track.addEventListener('ended', () => {
        if (streamRef.current !== mediaStream) return; // an older, already-replaced stream
        console.log('⚠️ Screen sharing stopped');
        stopCapture();
        setScreenShareLost(true);
      });
      setScreenShareLost(false);

      // Helper to start or restart the capture interval if not paused
      const startInterval = () => {
        if (captureIntervalRef.current) return;
        captureIntervalRef.current = setInterval(async () => {
        try {
          if (isCapturePausedRef.current) {
            return;
          }
          const bitmap = await imageCapture.grabFrame();
          const canvas = document.createElement("canvas");
          canvas.width = bitmap.width;
          canvas.height = bitmap.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(bitmap, 0, 0);
          
          // Debug: Log what's being captured
          console.log(`📸 Captured screenshot: ${bitmap.width}x${bitmap.height}`);
          console.log(`📸 Screenshot includes the reading content area`);
          
          const dataUrl = canvas.toDataURL("image/png");

          sendScreenshotToBackend(dataUrl);
        } catch (err) {
          console.error("Capture failed:", err);
        }
        }, 30000); // Capture every 30 seconds
      };

      startInterval();
      return true;

    } catch (err) {
      if (!resuming) {
        alert("Screen sharing is required to start the reading session.");
      }
      console.error("Permission denied or capture failed:", err);
      return false;
    }
  };

  // "Resume screen sharing" banner button: re-prompt for the screen (browsers
  // require a click for this) and continue capturing in the same session.
  const handleResumeScreenShare = async () => {
    const ok = await startContinuousCapture({ resuming: true });
    if (ok) console.log('✅ Screen sharing resumed');
  };

  const stopCapture = () => {
    if (captureIntervalRef.current) {
      clearInterval(captureIntervalRef.current);
      captureIntervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  const pauseCapture = () => {
    isCapturePausedRef.current = true;
    if (captureIntervalRef.current) {
      clearInterval(captureIntervalRef.current);
      captureIntervalRef.current = null;
    }
  };

  const resumeCapture = () => {
    isCapturePausedRef.current = false;
    // If we still have the stream, recreate the ImageCapture and interval
    if (streamRef.current) {
      const track = streamRef.current.getVideoTracks()[0];
      if (!track) return;
      const imageCapture = new ImageCapture(track);
      if (!captureIntervalRef.current) {
        captureIntervalRef.current = setInterval(async () => {
          try {
            if (isCapturePausedRef.current) return;
            const bitmap = await imageCapture.grabFrame();
            const canvas = document.createElement("canvas");
            canvas.width = bitmap.width;
            canvas.height = bitmap.height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(bitmap, 0, 0);
            const dataUrl = canvas.toDataURL("image/png");
            sendScreenshotToBackend(dataUrl);
          } catch (err) {
            console.error("Capture failed:", err);
          }
        }, 30000);
      }
    }
  };

  const sendScreenshotToBackend = (dataUrl) => {
    // Send the screenshot to backend via WebSocket
    sendScreenshot(dataUrl);
  };

  // CV Webcam handlers
  const startCv = async () => {
    try {
      if (!accessToken) {
        alert('No JWT found. Please sign in again.');
        return;
      }

      // Reset reconnection state when starting CV
      cvIntentionallyStoppedRef.current = false;
      reconnectAttemptsRef.current = 0;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Get webcam
      const videoEl = videoRef.current;
      const canvasEl = canvasRef.current;
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: { ideal: 640 },
          height: { ideal: 480 },
          frameRate: { ideal: 5 }
        } 
      });
      // Track the stream independently so stopCv() can stop it after unmount.
      cvStreamRef.current = stream;
      videoEl.srcObject = stream;
      
      // Wait for video to be ready and playing
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Video metadata timeout'));
        }, 10000);
        
        videoEl.onloadedmetadata = () => {
          canvasEl.width = videoEl.videoWidth;
          canvasEl.height = videoEl.videoHeight;
          clearTimeout(timeout);
          
          // Ensure video is playing (required for some browsers)
          videoEl.play().then(() => {
            console.log(`Video ready: ${videoEl.videoWidth}x${videoEl.videoHeight} @ ${videoEl.videoWidth > 0 ? 'ready' : 'unknown'} fps`);
            resolve();
          }).catch(reject);
        };
        
        videoEl.onerror = (err) => {
          clearTimeout(timeout);
          reject(err);
        };
      });

      // Get CV service URL from environment variable (set at build time)
      // If REACT_APP_CV_SERVICE_URL is empty (production), use current origin
      const cvBaseUrl = process.env.REACT_APP_CV_SERVICE_URL || window.location.origin;
      // Construct WebSocket URL
      let wsBaseUrl;
      let wsPath = '/ws'; // Default WebSocket path
      if (process.env.REACT_APP_CV_SERVICE_URL) {
        // Development: convert local CV URL (e.g. 4041) to matching ws scheme
        wsBaseUrl = process.env.REACT_APP_CV_SERVICE_URL.replace(/^http:/, 'ws:');
      } else {
        // Production: use current origin with appropriate protocol
        // Use /ws/cv path to route through nginx to CV service
        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        wsBaseUrl = `${wsProtocol}//${window.location.host}`;
        wsPath = '/ws/cv'; // Use nginx proxy path for CV WebSocket
      }

      // Start session (idempotent server-side)
      setCvStatus('starting');
      const startResp = await fetch(`${cvBaseUrl}/api/v1/cv/start-session`, { 
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
      if (!startResp.ok) {
        const text = await startResp.text();
        console.error('CV start failed:', startResp.status, text);
        setCvStatus(`error: start ${startResp.status}`);
        return;
      }
      const startData = await startResp.json();
      console.log('CV start response:', startData);

      // Poll for session status until ready
      setCvStatus('waiting for Python...');
      let sessionReady = false;
      let attempts = 0;
      const maxAttempts = 60; // 30 seconds max (500ms * 60)
      
      while (!sessionReady && attempts < maxAttempts) {
        try {
          const statusResp = await fetch(`${cvBaseUrl}/api/v1/cv/session-status`, {
            headers: {
              'Authorization': `Bearer ${accessToken}`
            }
          }); 
          
          if (statusResp.ok) {
            const statusData = await statusResp.json();
            console.log('CV status check:', statusData);
            
            if (statusData.status === 'ready') {
              sessionReady = true;
              setCvStatus('Python ready, connecting...');

              // Only replay the calibration animation for a genuinely new
              // backend session. A reconnect that reused an existing
              // session (start-session returned "already_running") means
              // the Python process, and the calibration it already ran,
              // never actually went away.
              if (calibrationInProgressRef.current) {
                // A reconnect landed while the animation from an earlier
                // startCv() call is still mid-sequence - let that one keep
                // running instead of stacking a second one on top of it.
                console.log('Calibration animation already in progress, skipping re-trigger from reconnect');
              } else if (!calibrationDoneRef.current || startData.status !== 'already_running') {
                console.log('CV service ready, starting calibration animation...');
                calibrationInProgressRef.current = true;
                setCalibrationStatus('starting');
                setTimeout(() => {
                  setShowCalibrationAnimation(true);
                  startCalibrationAnimation();
                }, 1000);
              } else {
                console.log('CV session reused after reconnect, skipping calibration replay');
                setCalibrationStatus('complete');
              }

              break;
            } else if (statusData.status === 'error') {
              setCvStatus(`error: Python failed to start`);
              return;
            }
          }
        } catch (statusError) {
          console.error('Status check error:', statusError);
        }
        
        attempts++;
        await new Promise(resolve => setTimeout(resolve, 500)); // Wait 500ms between polls
      }
      
      if (!sessionReady) {
        setCvStatus('error: Python timeout');
        return;
      }

      // Now open WebSocket connection
      const ws = new WebSocket(`${wsBaseUrl}${wsPath}?token=${encodeURIComponent(accessToken)}`);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WS open to CV');
        setCvStatus('streaming');
        
        // Reset reconnection attempts on successful connection
        reconnectAttemptsRef.current = 0;
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
        
        // Use a more reliable frame capture mechanism that works better in Chromium
        // Hybrid approach: use requestAnimationFrame for smooth capture, but throttle to 5fps
        let lastFrameTime = performance.now();
        const targetFPS = 5;
        const frameInterval = 1000 / targetFPS; // 200ms for 5fps
        let frameCount = 0;
        let startTime = performance.now();
        
        const captureFrame = (currentTime) => {
          if (ws.readyState !== WebSocket.OPEN) {
            frameTimerRef.current = null;
            return;
          }
          
          const elapsed = currentTime - lastFrameTime;
          
          // Only capture if enough time has passed (throttle to target FPS)
          if (elapsed >= frameInterval) {
            try {
              const ctx = canvasEl.getContext('2d');
              ctx.drawImage(videoEl, 0, 0);
              const dataURL = canvasEl.toDataURL('image/jpeg', 0.8);
              const base64Data = dataURL.split(',')[1];
              
              // Check if WebSocket buffer is not full (bufferedAmount < threshold)
              // This helps prevent frame drops in Chromium when network is slow
              if (ws.bufferedAmount < 2 * 1024 * 1024) { // 2MB buffer threshold
                ws.send(base64Data);
                lastFrameTime = currentTime;
                frameCount++;
                
                // Log FPS every 100 frames for debugging
                if (frameCount % 100 === 0) {
                  const actualFPS = (frameCount / (currentTime - startTime)) * 1000;
                  console.log(`CV streaming: ${frameCount} frames sent, ~${actualFPS.toFixed(1)} fps`);
                }
              } else {
                console.warn(`WebSocket buffer full (${(ws.bufferedAmount / 1024 / 1024).toFixed(2)}MB), skipping frame`);
              }
            } catch (err) {
              console.error('Frame capture error:', err);
            }
          }
          
          // Continue capturing using requestAnimationFrame for better browser compatibility
          // This works more reliably in Chromium than setInterval
          if (typeof requestAnimationFrame !== 'undefined') {
            frameTimerRef.current = requestAnimationFrame(captureFrame);
          } else {
            // Fallback to setTimeout if requestAnimationFrame is not available
            frameTimerRef.current = setTimeout(() => captureFrame(performance.now()), frameInterval);
          }
        };
        
        // Start capturing frames
        if (typeof requestAnimationFrame !== 'undefined') {
          frameTimerRef.current = requestAnimationFrame(captureFrame);
        } else {
          frameTimerRef.current = setTimeout(() => captureFrame(performance.now()), frameInterval);
        }
      };

      ws.onerror = (e) => {
        console.error('WS error', e);
        setCvStatus('ws error');
      };

      ws.onclose = (event) => {
        console.log('WS closed', event.code, event.reason);
        setCvStatus('closed');
        if (frameTimerRef.current) {
          if (typeof cancelAnimationFrame !== 'undefined') {
            cancelAnimationFrame(frameTimerRef.current);
          } else {
            clearTimeout(frameTimerRef.current);
          }
          frameTimerRef.current = null;
        }
        
        // Attempt to reconnect if not intentionally stopped
        if (!cvIntentionallyStoppedRef.current) {
          const maxReconnectAttempts = 10; // Maximum reconnection attempts
          const baseDelay = 1000; // Start with 1 second delay
          
          if (reconnectAttemptsRef.current < maxReconnectAttempts) {
            reconnectAttemptsRef.current += 1;
            // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 32s, 60s (capped at 60s)
            const delay = Math.min(baseDelay * Math.pow(2, reconnectAttemptsRef.current - 1), 60000);
            
            console.log(`🔄 Attempting to reconnect to CV service (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts}) in ${delay}ms...`);
            setCvStatus(`reconnecting (${reconnectAttemptsRef.current}/${maxReconnectAttempts})...`);
            
            reconnectTimeoutRef.current = setTimeout(() => {
              // Only reconnect if still not intentionally stopped
              if (!cvIntentionallyStoppedRef.current) {
                console.log('🔄 Reconnecting to CV service...');
                // Reconnect by calling startCv again (it will reuse existing session)
                startCv().catch(err => {
                  console.error('❌ Reconnection failed:', err);
                  setCvStatus(`reconnect failed: ${err.message}`);
                });
              }
            }, delay);
          } else {
            console.error('❌ Max reconnection attempts reached. Please manually restart CV.');
            setCvStatus('reconnect failed: max attempts');
          }
        }
      };
    } catch (err) {
      console.error('startCv error:', err);
      setCvStatus(`error: ${err.message}`);
    }
  };

  const stopCv = () => {
    try {
      // Mark as intentionally stopped to prevent auto-reconnection
      cvIntentionallyStoppedRef.current = true;
      
      // Clear any pending reconnection attempts
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      reconnectAttemptsRef.current = 0;
      
      const ws = wsRef.current;
      // Cancel both requestAnimationFrame and setTimeout
      if (frameTimerRef.current) {
        if (typeof cancelAnimationFrame !== 'undefined') {
          cancelAnimationFrame(frameTimerRef.current);
        } else {
          clearTimeout(frameTimerRef.current);
        }
        frameTimerRef.current = null;
      }
      if (ws && ws.readyState === WebSocket.OPEN) ws.close();
      // Stop the camera via the stream ref (works even if <video> is unmounted).
      if (cvStreamRef.current) {
        cvStreamRef.current.getTracks().forEach(t => t.stop());
        cvStreamRef.current = null;
      }
      const videoEl = videoRef.current;
      if (videoEl?.srcObject) {
        videoEl.srcObject.getTracks().forEach(t => t.stop());
        videoEl.srcObject = null;
      }
      setCvStatus('stopped');
      
      // Stop calibration animation and reset calibration state
      stopCalibrationAnimation();
      calibrationInProgressRef.current = false;
      setCalibrationStatus('idle');
      setShowCalibrationAnimation(false);
      setSpritePosition('center');
    } catch (e) {
      console.error('stopCv error:', e);
    }
  };

  // Release the camera on full-page unloads (tab close/refresh)
  useEffect(() => {
    const releaseCamera = () => {
      if (cvStreamRef.current) {
        cvStreamRef.current.getTracks().forEach(t => t.stop());
        cvStreamRef.current = null;
      }
    };
    window.addEventListener('pagehide', releaseCamera);
    window.addEventListener('beforeunload', releaseCamera);
    return () => {
      window.removeEventListener('pagehide', releaseCamera);
      window.removeEventListener('beforeunload', releaseCamera);
    };
  }, []);

  const checkCvStatus = async () => {
    if (!accessToken) {
      alert('No JWT found. Please sign in again.');
      return;
    }

    try {
      // Get CV service URL from environment variable (set at build time)
      // If REACT_APP_CV_SERVICE_URL is empty (production), use current origin
      const cvBaseUrl = process.env.REACT_APP_CV_SERVICE_URL || window.location.origin;
      const statusResp = await fetch(`${cvBaseUrl}/api/v1/cv/session-status`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
      
      if (statusResp.ok) {
        const statusData = await statusResp.json();
        console.log('Current CV status:', statusData);
        setCvStatus(statusData.status);
      } else {
        console.error('Status check failed:', statusResp.status);
        setCvStatus('status check failed');
      }
    } catch (error) {
      console.error('Status check error:', error);
      setCvStatus('status check error');
    }
  };

  // Calibration animation function (based on Calibration.js)
  const startCalibrationAnimation = () => {
    // Defensive: clear any stray timers from a previous run before starting
    // a new one, in case this ever gets invoked despite the in-progress guard.
    stopCalibrationAnimation();
    setCalibrationStatus('running');
    setSpritePosition('center');
    setCalibrationCountdown(5);

    // Initial countdown
    let countdown = 5;
    const countdownInterval = setInterval(() => {
      countdown--;
      setCalibrationCountdown(countdown);
      
      if (countdown <= 0) {
        clearInterval(countdownInterval);
        // Start the choreographed movement sequence
        startCalibrationSequence();
      }
    }, 1000);

    calibrationTimerRef.current = countdownInterval;
  };

  const startCalibrationSequence = () => {
    // Choreographed movement (10s timeline) - move to each corner
    const positions = ['top-right', 'top-left', 'bottom-left', 'bottom-right'];
    const durations = [4000, 4000, 4000, 4000]; // 2.5 seconds per corner = 10 seconds total
    let totalTime = 0;
    
    const timeouts = positions.map((pos, index) => {
      const timeout = setTimeout(() => {
        setSpritePosition(pos);
        if (index === positions.length - 1) {
          // Calibration complete
          setTimeout(() => {
            setCalibrationStatus('complete');
            setShowCalibrationAnimation(false);
            setSpritePosition('center');
            calibrationDoneRef.current = true;
            calibrationInProgressRef.current = false;
          }, durations[index]);
        }
      }, totalTime);
      totalTime += durations[index];
      return timeout;
    });

    // Store timeouts for cleanup
    calibrationTimerRef.current = timeouts;
  };

  const stopCalibrationAnimation = () => {
    if (calibrationTimerRef.current) {
      if (Array.isArray(calibrationTimerRef.current)) {
        calibrationTimerRef.current.forEach(clearTimeout);
      } else {
        clearInterval(calibrationTimerRef.current);
      }
      calibrationTimerRef.current = null;
    }
  };

  // LiveKit audio analysis setup
  const setupLiveKitAudioAnalysis = (audioElement) => {
    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContext();
      console.log('✓ LiveKit audio context created');
    }

    const source = audioContextRef.current.createMediaElementSource(audioElement);
    
    analyserRef.current = audioContextRef.current.createAnalyser();
    analyserRef.current.fftSize = 256;

    source.connect(analyserRef.current);
    analyserRef.current.connect(audioContextRef.current.destination);

    console.log('✓ LiveKit audio analyser connected');

    const canvas = visualizerCanvasRef.current;
    if (canvas) {
      canvas.width = canvas.clientWidth || 400;
      canvas.height = canvas.clientHeight || 80;
      drawLiveKitVisualizer();
    }
  };

  // Draw LiveKit audio visualizer
  const drawLiveKitVisualizer = () => {
    if (!analyserRef.current || !visualizerCanvasRef.current) return;

    const canvas = visualizerCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const analyser = analyserRef.current;

    animationFrameRef.current = requestAnimationFrame(drawLiveKitVisualizer);

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    analyser.getByteFrequencyData(dataArray);

    ctx.fillStyle = '#0f0f23';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const barWidth = (canvas.width / bufferLength) * 2.5;
    let x = 0;

    for (let i = 0; i < bufferLength; i++) {
      const barHeight = (dataArray[i] / 255) * canvas.height;
      ctx.fillStyle = `rgb(50, ${dataArray[i] + 100}, 150)`;
      ctx.fillRect(x, canvas.height - barHeight, barWidth, barHeight);
      x += barWidth + 1;
    }
  };

  // Add LiveKit transcript
  const addLiveKitTranscript = (text, type) => {
    setLiveKitTranscripts(prev => [...prev, { text, type, timestamp: new Date() }]);
    console.log(`📝 LiveKit transcript (${type}): ${text}`);
  };

  useEffect(() => {
    // Only connect to OCR WebSocket when using OCR pipeline
    if (useOCRPipeline) {
      initSocket("/ws/control", (data) => {
        console.log("ReadingSession update:", data);
        try {
          // data is already parsed JSON from socket.js
          if (data.type === "quiz_request") {
            console.log("Setting textHash:", data.hash_key);
            setTextHash(data.hash_key);  //  For Quiz 1–5
          } else if (data.type === "final_quiz_request") {
            console.log("Setting sessionHashes:", data.hashes);
            setSessionHashes(data.hashes);  //  For Final Quiz
          }
        } catch (e) {
          console.log("Error processing WebSocket message:", e, data);
        }
      });
    }

    // Resolve the student's Supabase user_id once, up front, and share the
    // same promise between the agent-control socket and the LiveKit room
    // connection below. Each used to call supabase.auth.getSession()
    // independently right after mount; the agent socket's call consistently
    // lost the race against the Supabase client's session restore (it fires
    // first, before the client finishes reading persisted session state),
    // so it always fell back to an anonymous id while LiveKit's slightly
    // later call always got the real one.
    const resolvedUserIdPromise = (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        return session?.user?.id || null;
      } catch (e) {
        console.error('Error getting user id for session scoping:', e);
        return null;
      }
    })();

    // Connect to Agent service WebSocket for inactivity-based auto mute.
    // user_id is passed through so agent-service can scope reading context
    // and activity tracking to this specific student instead of sharing one
    // global state across every concurrent session.
    const getAgentWebSocketUrl = (userId) => {
      const suffix = userId ? `?user_id=${encodeURIComponent(userId)}` : '';
      if (typeof window !== 'undefined') {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const host = window.location.host;
        return `${protocol}//${host}/ws/agent${suffix}`;
      }
      // Fallback for non-browser environments / local testing
      return `ws://localhost:8000/ws/agent${suffix}`;
    };

    const connectAgentSocket = async () => {
      const agentUserId = await resolvedUserIdPromise;

      try {
      const agentSocket = new WebSocket(getAgentWebSocketUrl(agentUserId));
      agentSocketRef.current = agentSocket;

      agentSocket.onopen = () => {
        console.log('Agent WebSocket connected');
      };

      agentSocket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('Agent WebSocket message:', data);

          if (data.type === 'auto_mute') {
            console.log('🔔 Received auto_mute from agent service');
            // Auto-mute microphone in LiveKit and update local UI state.
            // Rely only on the presence of a room; if the room is connected,
            // LiveKit will handle the mic state change.
            const room = liveKitRoomRef.current;
            if (room) {
              (async () => {
                try {
                  await room.localParticipant.setMicrophoneEnabled(false);
                  setIsMicrophoneMuted(true);
                  console.log('🔇 Microphone auto-muted due to inactivity');
                } catch (err) {
                  console.error('Error auto-muting microphone:', err);
                }
              })();
            } else {
              console.warn('auto_mute received but no LiveKit room is available');
            }
          }
        } catch (err) {
          console.error('Invalid JSON from agent WebSocket:', err);
        }
      };

      agentSocket.onclose = (event) => {
        console.log('Agent WebSocket closed:', event.code);
        agentSocketRef.current = null;
      };

      agentSocket.onerror = (err) => {
        console.error('Agent WebSocket error:', err);
      };
    } catch (err) {
      console.error('Failed to connect Agent WebSocket:', err);
    }
    };
    connectAgentSocket();

    // Connect to LiveKit voice assistant
    const connectLiveKit = async () => {
      try {
        console.log('🔌 Connecting to LiveKit voice assistant...');
        setLiveKitStatus('connecting');

        // Pass the Supabase user_id through as the LiveKit participant identity
        // so agent-service can match an inattentiveness webhook's user_id back
        // to this exact room (needed for correct routing with concurrent sessions).
        const liveKitUserId = await resolvedUserIdPromise;

        // fetchLiveKitToken will use production URL by default (proxied through nginx)
        const { token, url } = await fetchLiveKitToken(null, liveKitUserId);
        const room = new Room();

        // Connection state handler
        room.on(RoomEvent.ConnectionStateChanged, (state) => {
          setLiveKitConnectionState(state);
          setLiveKitStatus(state === ConnectionState.Connected ? 'connected' : state);
          
          if (state === ConnectionState.Connected) {
            console.log('✓ Connected to LiveKit');
            setLiveKitStatus('connected');
          }
        });

        // Track subscription handler
        room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
          if (track.kind === Track.Kind.Audio) {
            console.log(`✓ Audio track received from ${participant.identity}`);
            
            const audioElement = track.attach();
            audioElement.style.display = 'none';
            document.body.appendChild(audioElement);
            
            setupLiveKitAudioAnalysis(audioElement);
          }
        });

        // Data received handler
        room.on(RoomEvent.DataReceived, (payload, participant) => {
          try {
            const data = JSON.parse(new TextDecoder().decode(payload));
            if (data.type === 'transcription') {
              const isAgent = participant.identity.startsWith('agent') || 
                             participant.identity.includes('agent');
              addLiveKitTranscript(data.text, isAgent ? 'agent' : 'user');
            }
          } catch (e) {
            console.error('Error parsing LiveKit data:', e);
          }
        });

        // Transcription received handler
        room.on(RoomEvent.TranscriptionReceived, (segments, participant) => {
          segments.forEach(segment => {
            if (segment.final) {
              const isAgent = participant?.identity?.startsWith('agent') || 
                             participant?.identity?.includes('agent');
              const type = isAgent ? 'agent' : 'user';
              addLiveKitTranscript(segment.text, type);

              // If this is a user utterance, notify the agent service so it can
              // reset the inactivity timer based on actual speech activity.
              if (type === 'user' && agentSocketRef.current && agentSocketRef.current.readyState === WebSocket.OPEN) {
                try {
                  agentSocketRef.current.send(JSON.stringify({ type: 'user_activity' }));
                  console.log('Notified agent service: user_activity (from transcription)');
                } catch (err) {
                  console.error('Failed to notify agent service of user activity:', err);
                }
              }
            }
          });
        });

        await room.connect(url, token);
        liveKitRoomRef.current = room;
        await room.localParticipant.setMicrophoneEnabled(false);
        setIsMicrophoneMuted(true);
        console.log('✓ LiveKit microphone enabled');
      } catch (error) {
        console.error('❌ Failed to connect to LiveKit:', error);
        setLiveKitStatus(`error: ${error.message}`);
      }
    };

    connectLiveKit();

    return () => {
      if (useOCRPipeline) {
        stopCapture(); // Stop screen capture when component unmounts
        closeSocket();
      }
      stopCv(); // Stop CV when component unmounts

       // Close agent WebSocket if open
      if (agentSocketRef.current) {
        try {
          agentSocketRef.current.close();
        } catch (e) {
          console.error('Error closing agent WebSocket:', e);
        }
        agentSocketRef.current = null;
      }
      
      // Disconnect LiveKit
      if (liveKitRoomRef.current) {
        liveKitRoomRef.current.disconnect().catch(console.error);
        liveKitRoomRef.current = null;
        setIsMicrophoneMuted(false);
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close().catch(console.error);
        audioContextRef.current = null;
      }
      if (analyserRef.current) {
        analyserRef.current = null;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      
      // Clear visualizer
      if (visualizerCanvasRef.current) {
        const canvas = visualizerCanvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    };
  }, []);

  // Cleanup calibration animation on unmount
  useEffect(() => {
    return () => {
      stopCalibrationAnimation();
      stopCv();
    };
  }, []);

  // Avatar overlay animation - expand when avatar talks, default to small
  // No automatic toggle - only triggered by WebSocket notification

  // Get access token for CV service
  useEffect(() => {
    const getAccessToken = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          setAccessToken(session.access_token);
        }
      } catch (error) {
        console.error('Error getting access token:', error);
      }
    };

    getAccessToken();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session?.access_token) {
        setAccessToken(session.access_token);
      } else {
        setAccessToken(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const readingButtons = [
    { id: 1, label: 'Start Learning', icon: '🎓', path: '/reading-session' },
    { id: 2, label: 'Dashboard', icon: '📊' },
    { id: 3, label: 'Help', icon: '❓' }
  ];

  const handleNavClick = async (button) => {
    if (button.label === 'Dashboard') {
      // Stop screen capture before navigating away
      // NO cache clearing - preserve session data
      if (useOCRPipeline) {
        stopCapture();
        sendControl("stop");
      }
      // Navigate to Student Dashboard
      navigate('/student');
    } else if (button.label === 'Help') {
      alert('Help:\n\n1. Select a topic from the left sidebar\n2. Click "Start Reading" to begin\n3. Read the content in the right panel\n4. Take quizzes at regular intervals\n5. Complete the Final Quiz when done\n\nFor technical support, contact your teacher.');
    }
  };
  // Handle automatic quiz trigger at specific time intervals
  const handleAutoQuizTrigger = (quizIndex) => {
    // Don't cut off an extra quiz in progress — open this one as soon as it closes.
    if (extraQuizOpenRef.current) {
      console.log(`⏰ Quiz ${quizIndex + 1} due during an extra quiz — opening it when the extra quiz closes`);
      pendingScheduledQuizRef.current = quizIndex;
      return;
    }
    console.log(`⏰ Auto-opening Quiz ${quizIndex + 1} (forced)`);
    setExtraQuizNumber(null);
    setCurrentQuizIndex(quizIndex);
    setShowQuiz(true);
    if (useOCRPipeline) {
      sendControl(`quiz_${quizIndex}`);
      sendControl("pause");
      pauseCapture();
    }
  };

  // New session: forget extra quizzes from the previous one.
  const resetExtraQuizzes = () => {
    extraQuizCountRef.current = 0;
    extraQuizOpenRef.current = false;
    pendingScheduledQuizRef.current = null;
    setExtraQuizNumber(null);
    try { sessionStorage.setItem(EXTRA_QUIZ_COUNT_KEY, '0'); } catch (e) { /* ignore */ }
  };

  // "Extra Quiz" button (timed mode): an on-demand quiz on top of the schedule.
  const handleExtraQuiz = () => {
    if (showQuiz) return;
    const number = ++extraQuizCountRef.current;
    try { sessionStorage.setItem(EXTRA_QUIZ_COUNT_KEY, String(number)); } catch (e) { /* review falls back to scheduled count */ }
    console.log(`➕ Opening Extra Quiz #${number}`);
    extraQuizOpenRef.current = true;
    setExtraQuizNumber(number);
    setShowQuiz(true);
    if (useOCRPipeline) {
      sendControl("pause");
      pauseCapture();
    }
  };

  // Close the quiz overlay, resume capture, and open any scheduled quiz that came
  // due while an extra quiz was open.
  const handleQuizClosed = () => {
    // NO cache clearing - preserve session data
    console.log('📝 Quiz completed - resuming capture');
    setShowQuiz(false);
    if (useOCRPipeline) {
      sendControl("resume");  // Resume OCR pipeline
      resumeCapture();
    }
    extraQuizOpenRef.current = false;
    const pending = pendingScheduledQuizRef.current;
    if (pending !== null) {
      pendingScheduledQuizRef.current = null;
      handleAutoQuizTrigger(pending);
    }
  };

  // Handle manual quiz trigger (the "Quiz Me" button in manual mode). Advances
  // through the same regular-quizzes-then-final sequence; after the final it
  // loops back to index 0.
  const manualQuizIndexRef = useRef(0);
  const handleManualQuizTrigger = () => {
    const idx = manualQuizIndexRef.current;
    console.log(`📝 Quiz Me: opening Quiz ${idx + 1} (manual)`);
    handleAutoQuizTrigger(idx);
    // Advance for next click; loop back to 0 after the final.
    manualQuizIndexRef.current = idx >= FINAL_QUIZ_INDEX ? 0 : idx + 1;
  };


  const handleTopicSelect = (url, subject, topic) => {
    setTopicUrl(url);
    setSelectedSubject(subject);
    setSelectedTopic(topic);

    // Notify agent-service of selected document
    if (agentSocketRef.current && agentSocketRef.current.readyState === WebSocket.OPEN) {
      agentSocketRef.current.send(JSON.stringify({
        type: 'reading_context_update',
        document_name: topic.label || '',
        page: 1,
      }));
    }
  };

  // Track current page for quiz generation
  const handlePageChange = (page) => {
    setCurrentPage(page);

    // Notify agent-service of current reading position
    if (agentSocketRef.current && agentSocketRef.current.readyState === WebSocket.OPEN && selectedTopic) {
      agentSocketRef.current.send(JSON.stringify({
        type: 'reading_context_update',
        document_name: selectedTopic.label || '',
        page: page,
      }));
    }
  };

  const handleStart = async () => {
    try {
      console.log('🚀 Starting new reading session...');
      
      // Start screen capture only when using OCR pipeline
      if (useOCRPipeline) {
        await startContinuousCapture();
      }
      
      setShowLeft(false);
      setStartTimer(true); // Trigger timer start
      // Reset the trigger after a brief moment
      setTimeout(() => setStartTimer(false), 100);
      // Reset quiz index when starting a new reading session
      setCurrentQuizIndex(0);
      quizHistoryRef.current = { hashes: new Set(), questions: new Set() };
      resetExtraQuizzes();
      
      // Send start command to backend to launch OCR pipeline (only if using OCR)
      if (useOCRPipeline) {
        sendControl("start");
      }
      
      // Start CV webcam automatically (calibration animation will trigger when ready)
      await startCv();
      
      console.log('✅ Reading session started successfully');
      
    } catch (error) {
      console.error('❌ Error starting reading session:', error);
      // Continue with normal flow
      if (useOCRPipeline) {
        await startContinuousCapture();
      }
      setShowLeft(false);
      setStartTimer(true);
      setTimeout(() => setStartTimer(false), 100);
      setCurrentQuizIndex(0);
      quizHistoryRef.current = { hashes: new Set(), questions: new Set() };
      resetExtraQuizzes();
      if (useOCRPipeline) {
        sendControl("start");
      }
      await startCv();
    }
  };

  const handleFinalQuizComplete = async () => {
    console.log('🏁 Reading session quiz completed - clearing cache and returning to Student Dashboard');
    
    // Clear cache after final quiz completion
    try {
      console.log('🔄 Clearing cache files after final quiz completion...');
      const cacheResult = await refreshCache();
      if (cacheResult.success) {
        console.log('✅ Cache cleared successfully after final quiz');
      } else {
        console.error('⚠️  Cache clearing had issues:', cacheResult.error);
      }
    } catch (error) {
      console.error('❌ Error clearing cache:', error);
      // Continue with navigation even if cache clearing fails
    }
    
    setShowQuiz(false);
    if (useOCRPipeline) {
      sendControl("resume");  // Resume OCR pipeline
    }
    
    // Navigate to Student Dashboard (state triggers review prompt popup)
    navigate('/student', { state: { showReviewPromptPopup: true } });
  };


  const handleDashboardComplete = () => {
    setShowDashboard(false);
    setCurrentQuizIndex(0);
  };

  /*// Show Dashboard
  if (showDashboard) {
    return (
      <div className="reading-session">
        <QuizDashboard onStartNewQuiz={handleDashboardComplete} />
      </div>
    );
  }*/

  return (
    <div className="reading-session">
      <Header buttons={readingButtons} onNavClick={handleNavClick} activeButton={1} />
      <div className="reading-main-content">
        {showLeft && <LeftSection onTopicSelect={handleTopicSelect} onStart={handleStart} />}
        <RightSection
          content={content}
          file={file}
          topicUrl={topicUrl}
          startTimer={startTimer}
          onAutoQuizTrigger={handleAutoQuizTrigger}
          onManualQuizTrigger={handleManualQuizTrigger}
          onExtraQuiz={handleExtraQuiz}
          quizOpen={showQuiz}
          quizMode={QUIZ_MODE}
          avatarOverlayLarge={avatarOverlayLarge}
          liveKitRoomRef={liveKitRoomRef}
          liveKitConnectionState={liveKitConnectionState}
          isMicrophoneMuted={isMicrophoneMuted}
          onPageChange={handlePageChange}
          onToggleMute={async () => {
            try {
              if (liveKitRoomRef.current && liveKitConnectionState === ConnectionState.Connected) {
                const newMuteState = !isMicrophoneMuted;
                await liveKitRoomRef.current.localParticipant.setMicrophoneEnabled(!newMuteState);
                setIsMicrophoneMuted(newMuteState);
                console.log(newMuteState ? '🔇 Microphone muted' : '🎤 Microphone unmuted');

                // Notify agent service when the user goes to unmuted state,
                // so it can reset the inactivity timer.
                if (!newMuteState && agentSocketRef.current && agentSocketRef.current.readyState === WebSocket.OPEN) {
                  try {
                    agentSocketRef.current.send(JSON.stringify({ type: 'user_unmuted' }));
                    console.log('Notified agent service: user_unmuted');
                  } catch (err) {
                    console.error('Failed to notify agent service of unmute:', err);
                  }
                }
              }
            } catch (error) {
              console.error('Error toggling microphone:', error);
            }
          }}
        />
      </div>
      
      {/* Quiz Overlay */}
      <ModalPortal isOpen={showQuiz} onClose={handleQuizClosed}>
        <div className="quiz-overlay-container">
          <QuizInterface 
            key={extraQuizNumber ? `extra-${extraQuizNumber}` : `quiz-${currentQuizIndex}`}
            onClose={handleQuizClosed}
            currentQuizIndex={currentQuizIndex}
            extraQuizNumber={extraQuizNumber}
            textHash={textHash}
            sessionHashes={sessionHashes}
            quizHistory={quizHistoryRef.current}
            selectedSubject={selectedSubject}
            selectedTopic={selectedTopic}
            currentPage={currentPage}
            onFinalQuizComplete={handleFinalQuizComplete}
            onQuizResultSaved={handleQuizResultSaved}
          />
        </div>
      </ModalPortal>

      {/* Hidden video and canvas elements for CV functionality */}
      <video 
        ref={videoRef} 
        autoPlay 
        muted 
        style={{ display: 'none' }}
      />
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {/* Screen sharing lost mid-session - let the student resume without restarting */}
      {screenShareLost && !showLeft && (
        <div role="alert" style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          maxWidth: 'calc(100vw - 32px)',
          padding: '12px 16px',
          background: '#fef3c7',
          border: '1px solid #fde68a',
          borderRadius: '10px',
          zIndex: 999, // below the quiz overlay (1000)
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontSize: '15px', fontWeight: 500, color: '#92400e' }}>
            Screen sharing stopped. Your session and quizzes are still running.
          </span>
          <button
            className="app-btn"
            onClick={handleResumeScreenShare}
            style={{ padding: '8px 16px', borderRadius: '8px', backgroundColor: '#2C5057', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}
          >
            Resume screen sharing
          </button>
        </div>
      )}

      {/* CV Status Indicator - show when study session is active */}
      {SHOW_CV_STATUS_INDICATOR && !showLeft && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '12px 20px',
          background: cvStatus === 'streaming' ? '#f0fdf4' : '#fef3c7',
          borderRadius: '8px',
          border: cvStatus === 'streaming' ? '1px solid #bbf7d0' : '1px solid #fde68a',
          zIndex: 1000,
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: cvStatus === 'streaming' ? '#10b981' : '#f59e0b'
          }}></div>
          <span style={{
            fontSize: '14px',
            fontWeight: '500',
            color: cvStatus === 'streaming' ? '#065f46' : '#92400e'
          }}>
            CV: {cvStatus === 'streaming' ? 'Active' : cvStatus}
          </span>
        </div>
      )}

      {/* Calibration Animation Overlay */}
      {showCalibrationAnimation && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          zIndex: 2000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {/* Message above image */}
          {calibrationStatus === 'running' && spritePosition === 'center' && (
            <div style={{
              position: 'absolute',
              top: '20%',
              left: '50%',
              transform: 'translate(-50%, 0)',
              textAlign: 'center',
              zIndex: 10,
              color: 'white',
              fontSize: '24px',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)'
            }}>
              Calibration starting, follow the cat!
            </div>
          )}
          
          {calibrationStatus === 'complete' && (
            <div style={{
              position: 'absolute',
              top: '20%',
              left: '50%',
              transform: 'translate(-50%, 0)',
              textAlign: 'center',
              zIndex: 10,
              color: 'white',
              fontSize: '24px',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)'
            }}>
              Calibration complete!
            </div>
          )}

          {/* Cat Image */}
          <img
            src={require('../assets/images/cat-persona.png')}
            alt="Calibration Guide"
            style={{
              width: '200px',
              height: '200px',
              objectFit: 'contain',
              transition: 'all 0.5s ease-in-out',
              position: 'absolute',
              top: spritePosition === 'center' ? '50%' :
                   spritePosition.includes('top') ? '0' : 'auto',
              bottom: spritePosition.includes('bottom') ? '0' : 'auto',
              left: spritePosition === 'center' ? '50%' :
                    spritePosition.includes('left') ? '0' : 'auto',
              right: spritePosition.includes('right') ? '0' : 'auto',
              transform: spritePosition === 'center' ? 'translate(-50%, -50%)' :
                        spritePosition.includes('top') && spritePosition.includes('left') ? 'translate(0, 0)' :
                        spritePosition.includes('top') && spritePosition.includes('right') ? 'translate(0, 0)' :
                        spritePosition.includes('bottom') && spritePosition.includes('left') ? 'translate(0, 0)' :
                        spritePosition.includes('bottom') && spritePosition.includes('right') ? 'translate(0, 0)' : 'translate(-50%, -50%)'
            }}
          />

          {/* Countdown below image */}
          {calibrationStatus === 'running' && spritePosition === 'center' && (
            <div style={{
              position: 'absolute',
              top: '60%',
              left: '50%',
              transform: 'translate(-50%, 0)',
              color: 'white',
              fontSize: '48px',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)'
            }}>
              {calibrationCountdown}
            </div>
          )}
        </div>
      )}

    </div>
  );
}

export default ReadingSession;
